import { Fragment, type ReactNode } from 'react';

/**
 * Markdown a React sin HTML crudo (arquitectura 2.12): documentos legales, Manual y base de
 * conocimiento. Soporta títulos (##, ###), párrafos, **negritas**, *cursivas*, `código`,
 * [enlaces](https://…), listas con - o 1., citas (>) y tablas con |. Todo texto se escapa porque
 * React nunca interpreta HTML: un «<script>» en el contenido se muestra como texto.
 */
export function Markdown({ texto, className }: { texto: string; className?: string }) {
  return <div className={className ?? 'markdown'}>{bloques(texto)}</div>;
}

/** Solo enlaces http(s), mailto o rutas relativas; lo demás se muestra como texto. */
const enlaceSeguro = (url: string) => /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(url);

export function enLinea(texto: string): ReactNode[] {
  const salida: ReactNode[] = [];
  const patron = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let ultimo = 0;
  let k = 0;
  for (const m of texto.matchAll(patron)) {
    const i = m.index ?? 0;
    if (i > ultimo) salida.push(texto.slice(ultimo, i));
    const t = m[0];
    if (t.startsWith('**')) salida.push(<strong key={k++}>{enLinea(t.slice(2, -2))}</strong>);
    else if (t.startsWith('`')) salida.push(<code key={k++}>{t.slice(1, -1)}</code>);
    else if (t.startsWith('[')) {
      const [, etiqueta = '', url = ''] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t) ?? [];
      salida.push(
        enlaceSeguro(url) ? (
          <a
            key={k++}
            href={url}
            {...(/^https?:/i.test(url) && { target: '_blank', rel: 'noopener noreferrer' })}
          >
            {etiqueta}
          </a>
        ) : (
          <Fragment key={k++}>{etiqueta}</Fragment>
        ),
      );
    } else salida.push(<em key={k++}>{enLinea(t.slice(1, -1))}</em>);
    ultimo = i + t.length;
  }
  if (ultimo < texto.length) salida.push(texto.slice(ultimo));
  return salida;
}

const celdas = (linea: string) =>
  linea
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

function bloques(texto: string): ReactNode[] {
  const lineas = texto.replace(/\r\n?/g, '\n').split('\n');
  const salida: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lineas.length) {
    const linea = lineas[i] ?? '';
    if (!linea.trim()) {
      i++;
      continue;
    }
    const titulo = /^(#{1,4})\s+(.*)$/.exec(linea);
    if (titulo) {
      const nivel = Math.min(4, titulo[1]?.length ?? 2);
      const contenido = enLinea(titulo[2] ?? '');
      salida.push(
        nivel <= 2 ? (
          <h2 key={k++}>{contenido}</h2>
        ) : nivel === 3 ? (
          <h3 key={k++}>{contenido}</h3>
        ) : (
          <h4 key={k++}>{contenido}</h4>
        ),
      );
      i++;
      continue;
    }
    if (linea.trim().startsWith('|') && /^\s*\|?\s*:?-{2,}/.test(lineas[i + 1] ?? '')) {
      const cabeza = celdas(linea);
      const filas: string[][] = [];
      i += 2;
      while (i < lineas.length && (lineas[i] ?? '').trim().startsWith('|')) {
        filas.push(celdas(lineas[i] ?? ''));
        i++;
      }
      salida.push(
        <table key={k++}>
          <thead>
            <tr>
              {cabeza.map((c, j) => (
                <th key={j}>{enLinea(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((f, r) => (
              <tr key={r}>
                {f.map((c, j) => (
                  <td key={j}>{enLinea(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }
    if (/^\s*>/.test(linea)) {
      const cita: string[] = [];
      while (i < lineas.length && /^\s*>/.test(lineas[i] ?? '')) {
        cita.push((lineas[i] ?? '').replace(/^\s*>\s?/, ''));
        i++;
      }
      salida.push(<blockquote key={k++}>{bloques(cita.join('\n'))}</blockquote>);
      continue;
    }
    const lista = /^\s*([-*]|\d+\.)\s+/.exec(linea);
    if (lista) {
      const ordenada = /\d/.test(lista[1] ?? '');
      const elementos: string[] = [];
      while (i < lineas.length && /^\s*([-*]|\d+\.)\s+/.test(lineas[i] ?? '')) {
        elementos.push((lineas[i] ?? '').replace(/^\s*([-*]|\d+\.)\s+/, ''));
        i++;
      }
      const items = elementos.map((e, j) => <li key={j}>{enLinea(e)}</li>);
      salida.push(ordenada ? <ol key={k++}>{items}</ol> : <ul key={k++}>{items}</ul>);
      continue;
    }
    const parrafo: string[] = [];
    while (
      i < lineas.length &&
      (lineas[i] ?? '').trim() &&
      !/^(#{1,4}\s|\s*>|\s*([-*]|\d+\.)\s+|\s*\|)/.test(lineas[i] ?? '')
    ) {
      parrafo.push((lineas[i] ?? '').trim());
      i++;
    }
    salida.push(<p key={k++}>{enLinea(parrafo.join(' '))}</p>);
  }
  return salida;
}
